if (!customElements.get('rendi-quantity-input')) {
  class RendiQuantityInput extends HTMLElement {
    constructor() {
      super();
      this.hasInteracted = false;
      this.handleDecreaseBound = this.handleDecrease.bind(this);
      this.handleIncreaseBound = this.handleIncrease.bind(this);
      this.handleInputBound = this.handleInput.bind(this, false);
      this.handleBlurBound = this.handleInput.bind(this, true);
    }

    connectedCallback() {
      this.name = this.dataset.name || 'quantity';
      this.min = Number(this.dataset.min || 10);
      this.max = Number(this.dataset.max || Number.POSITIVE_INFINITY);
      this.input = this.querySelector('[data-quantity-input]');
      this.decreaseButton = this.querySelector('[data-quantity-decrease]');
      this.increaseButton = this.querySelector('[data-quantity-increase]');
      this.error = this.querySelector('[data-quantity-error]');

      this.setupAccessibility();
      this.syncControlsState(this.getValue());

      this.decreaseButton?.addEventListener('click', this.handleDecreaseBound);
      this.increaseButton?.addEventListener('click', this.handleIncreaseBound);
      this.input?.addEventListener('input', this.handleInputBound);
      this.input?.addEventListener('change', this.handleInputBound);
      this.input?.addEventListener('blur', this.handleBlurBound);
    }

    disconnectedCallback() {
      this.decreaseButton?.removeEventListener('click', this.handleDecreaseBound);
      this.increaseButton?.removeEventListener('click', this.handleIncreaseBound);
      this.input?.removeEventListener('input', this.handleInputBound);
      this.input?.removeEventListener('change', this.handleInputBound);
      this.input?.removeEventListener('blur', this.handleBlurBound);
    }

    getValue() {
      return Number(this.input?.value || 0);
    }

    handleDecrease() {
      this.hasInteracted = true;
      const currentValue = this.getValue();

      if (!currentValue || currentValue <= this.min) {
        this.setValue(this.min);
        return;
      }

      this.setValue(currentValue - 1);
    }

    handleIncrease() {
      this.hasInteracted = true;
      const currentValue = this.getValue();

      if (currentValue < this.min) {
        this.setValue(this.min);
        return;
      }

      this.setValue(currentValue + 1);
    }

    handleInput(validateOnBlur) {
      this.hasInteracted = true;
      this.setValue(this.input?.value ?? '', {
        validateOnBlur
      });
    }

    setupAccessibility() {
      if (!this.input) return;

      this.input.min = String(this.min);

      if (this.error && !this.error.id) {
        this.error.id = `${this.name}-quantity-error`;
      }

      if (this.error?.id) {
        this.input.setAttribute('aria-describedby', this.error.id);
      }
    }

    syncControlsState(value) {
      if (this.decreaseButton) {
        this.decreaseButton.disabled = value <= this.min;
      }
      if (this.increaseButton) {
        this.increaseButton.disabled = value >= this.max;
      }
    }

    updateValidationState({ isEmpty, isValid, validateOnBlur = false }) {
      if (this.input) {
        this.input.setAttribute('aria-invalid', !isEmpty && !isValid ? 'true' : 'false');
      }

      if (this.error) {
        const shouldShowError = !isEmpty && !isValid && (validateOnBlur || this.hasInteracted);
        this.error.hidden = !shouldShowError;
      }
    }

    setValue(value, options = {}) {
      const hasValue = value !== '' && value !== null && value !== undefined;
      // Counts are whole items: intake takes integers only.
      const parsedValue = Math.trunc(Number(value));
      // A min of 0 lets the field hold 0; otherwise 1 is the floor, as before.
      const floor = Math.min(this.min, 1);
      const boundedValue = hasValue && Number.isFinite(parsedValue)
        ? Math.min(this.max, Math.max(floor, parsedValue))
        : 0;
      const numericValue = Number.isFinite(boundedValue) ? boundedValue : 0;
      const isEmpty = !hasValue || numericValue === 0;
      const isValid = numericValue >= this.min;

      if (this.input) {
        this.input.value = hasValue && numericValue >= floor ? numericValue : '';
      }

      this.syncControlsState(numericValue);
      this.updateValidationState({
        isEmpty,
        isValid,
        validateOnBlur: Boolean(options.validateOnBlur)
      });

      if (!options.silent) {
        this.dispatchEvent(new CustomEvent('rendi:field-change', {
          bubbles: true,
          detail: {
            name: this.name,
            value: numericValue
          }
        }));
      }
    }
  }

  customElements.define('rendi-quantity-input', RendiQuantityInput);
}
